"use client";

import messages from "@/i18n/messages/en.json";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { CircleCheck, MapPin, Pencil } from "lucide-react";
import { Badge } from "@artha/ui";

import { RESUME_WORK_MODES } from "../../../personalization.constants";
import {
  safeProfileLink,
  profileLinkLabel,
  profileLinkIcon,
  profileLatestRole,
} from "../profileCards.utils";
import { ProfileEditorPopup } from "../editors/ProfileEditorPopup";
import {
  PROFILE_CARD_ACTION_ICON_CLASS_NAME,
  PROFILE_CARD_CLASS_NAME,
  PROFILE_CARD_EDIT_CLASS_NAME,
  PROFILE_CONTACT_VERIFIED_CLASS_NAME,
  PROFILE_CONTACT_VERIFY_CLASS_NAME,
  PROFILE_CHIP_CLASS_NAME,
} from "../profileCards.constants";
import type { ProfileBasicsSectionProps } from "../profileCards.types";

import { ProfileBasicsEditor } from "../editors/ProfileBasicsEditor";
export function ProfileBasicsSection(props: ProfileBasicsSectionProps) {
  const { profile } = props;
  const latestRole = profileLatestRole(profile);
  const t = useTranslations("profile.resumePersonalization");
  const location = [
    profile.location?.city,
    profile.location?.state,
    profile.location?.country,
  ]
    .filter(Boolean)
    .join(", ");
  return (
    <>
      <section
        aria-label={profile.name || "Profile"}
        className={PROFILE_CARD_CLASS_NAME}
      >
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="break-words text-xl font-bold leading-7 text-[#101828]">
                {profile.name ||
                  (props.readOnly
                    ? messages.talentNetworkUi.name_not_provided
                    : messages.talentNetworkUi.add_your_name)}
              </h2>
              {latestRole ? (
                <p className="mt-1 text-sm leading-5 text-[#475467]">
                  {latestRole}
                </p>
              ) : null}
              {location ? (
                <p className="mt-2 flex items-start gap-1.5 text-xs leading-5 text-[#667085]">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {location}
                </p>
              ) : null}
            </div>
            {!props.readOnly ? (
              <button
                type="button"
                aria-label={messages.talentNetworkUi.edit_profile}
                className={PROFILE_CARD_EDIT_CLASS_NAME}
                disabled={props.disabled}
                onClick={props.onEdit}
              >
                <Pencil
                  aria-hidden="true"
                  className={PROFILE_CARD_ACTION_ICON_CLASS_NAME}
                />
              </button>
            ) : null}
          </div>
          {profile.links?.length ? (
            <div
              aria-label={messages.talentNetworkUi.profile_links}
              className="flex flex-wrap items-center gap-4"
            >
              {profile.links.map((link, index) => {
                const url = safeProfileLink(link.url);
                const LinkIcon = profileLinkIcon(link.url);
                return url ? (
                  <a
                    key={`${url}-${index}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.label || profileLinkLabel(url)}
                    title={link.label || profileLinkLabel(url)}
                    className="inline-flex shrink-0 text-[#0D41E1] hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D41E1]"
                  >
                    <span aria-hidden="true" className="inline-flex shrink-0">
                      {typeof LinkIcon === "string" ? (
                        <Image
                          src={LinkIcon}
                          alt=""
                          width={20}
                          height={20}
                          className="h-5 w-5"
                        />
                      ) : (
                        <LinkIcon className="h-5 w-5" />
                      )}
                    </span>
                  </a>
                ) : null;
              })}
            </div>
          ) : null}
          <div className="space-y-1 border-t border-[#EAECF0] pt-3 text-xs leading-5 text-[#475467]">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
              <p className="min-w-0 break-all">
                {profile.email ||
                  (props.readOnly
                    ? messages.talentNetworkUi.email_not_provided
                    : messages.talentNetworkUi.add_your_email)}
              </p>
              {profile.email && props.emailVerified ? (
                <span
                  role="img"
                  aria-label={messages.talentNetworkUi.email_verified_2}
                  title={messages.talentNetworkUi.email_verified_2}
                  className={PROFILE_CONTACT_VERIFIED_CLASS_NAME}
                >
                  <CircleCheck
                    aria-hidden="true"
                    className="h-4 w-4"
                    strokeWidth={3}
                  />
                </span>
              ) : profile.email && !props.readOnly && props.onVerifyEmail ? (
                <button
                  type="button"
                  aria-label={
                    props.sendingEmail
                      ? messages.talentNetworkUi.sending_email_link
                      : props.emailResendSeconds
                        ? `Resend email in ${props.emailResendSeconds}s`
                        : messages.talentNetworkUi.verify_email
                  }
                  disabled={
                    props.disabled ||
                    props.editing ||
                    props.sendingEmail ||
                    Boolean(props.emailResendSeconds)
                  }
                  className={PROFILE_CONTACT_VERIFY_CLASS_NAME}
                  onClick={props.onVerifyEmail}
                >
                  {props.sendingEmail
                    ? messages.talentNetworkUi.sending
                    : props.emailResendSeconds
                      ? `Resend in ${props.emailResendSeconds}s`
                      : "Verify"}
                </button>
              ) : null}
            </div>
            {profile.phone || (!props.readOnly && props.onVerifyPhone) ? (
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                <p className="min-w-0 break-all">
                  {profile.phone || "Add your phone number"}
                </p>
                {profile.phone && props.phoneVerified ? (
                  <span
                    role="img"
                    aria-label={messages.talentNetworkUi.phone_verified_2}
                    title={messages.talentNetworkUi.phone_verified_2}
                    className={PROFILE_CONTACT_VERIFIED_CLASS_NAME}
                  >
                    <CircleCheck
                      aria-hidden="true"
                      className="h-4 w-4"
                      strokeWidth={3}
                    />
                  </span>
                ) : !props.readOnly && props.onVerifyPhone ? (
                  <button
                    type="button"
                    aria-label={messages.talentNetworkUi.verify_phone}
                    className={PROFILE_CONTACT_VERIFY_CLASS_NAME}
                    disabled={props.disabled || props.editing}
                    onClick={props.onVerifyPhone}
                  >
                    {messages.talentNetworkUi.verify}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          {props.readOnly && props.contactFooter ? (
            <div className="text-xs leading-5 text-[#475467]">
              {props.contactFooter}
            </div>
          ) : null}
          {profile.workMode ? (
            <div className="flex flex-wrap gap-2">
              {profile.workMode ? (
                <Badge
                  variant="blue"
                  label={
                    RESUME_WORK_MODES.includes(
                      profile.workMode as (typeof RESUME_WORK_MODES)[number],
                    )
                      ? t(`review.workModeOptions.${profile.workMode}`)
                      : profile.workMode
                  }
                  className={PROFILE_CHIP_CLASS_NAME}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
      {!props.readOnly && props.onCancel ? (
        <ProfileEditorPopup
          isOpen={props.editing}
          title={messages.talentNetworkUi.edit_profile}
          error={props.error}
          onClose={props.onCancel}
        >
          <ProfileBasicsEditor {...props} />
        </ProfileEditorPopup>
      ) : null}
    </>
  );
}
